import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import { useHover } from 'ahooks';
import { memo, useCallback, useMemo, useRef } from 'react';
import { SearchFilter } from '../..';
import { ClearTagsButton } from '../../components/filter-tags/components/clear-tags-button';
import { ClearButtonType } from '../../components/filter-tags/components/clear-tags-button/types';
import { useFilterTags } from '../../components/filter-tags/hooks/use-filter-tags';
import {
  buildClearAllDraft,
  getTagsCount,
} from '../../components/filter-tags/utils';
import { AdvancedBulkInputDialog } from '../autocomplete-filter/components/advanced-bulk-input-dialog';
import { useRenderChildrenFilter } from '../children-filter/use-render-children-filter';
import type { GroupChildrenFilterProps as Props } from './types';

export const GroupChildrenFilter = memo(
  ({ className, open, ...filter }: Props) => {
    const content = useRenderChildrenFilter(filter, open ?? false);
    const selectKeys = useMemo(
      () => filter.items?.map((i) => i.field!).filter(Boolean) ?? [],
      [filter.items]
    );
    const { setParams, params } = useSearchQueryFilter({ select: selectKeys });
    const tags = useFilterTags(params, filter as SearchFilter);
    const count = getTagsCount(tags);
    const hoverRef = useRef<HTMLDivElement>(null);
    const hover = useHover(hoverRef);
    const advancedDialogState = useBoolean(false);

    const handleClear = useCallback(() => {
      const draft = buildClearAllDraft(tags);
      setParams(draft);
    }, [tags, setParams]);

    const handleAdvancedButtonClick = useCallback(() => {
      advancedDialogState.onTrue();
    }, [advancedDialogState]);

    const handleAdvancedButtonMouseDown = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      },
      []
    );

    const showClearButton = useMemo(() => !!count && hover, [count, hover]);

    if (!open || !content) return null;

    return (
      <Card
        className={classnames('GroupChildrenFilter', className)}
        variant="filled"
        color="default"
        ref={hoverRef}
      >
        <CardHeader
          padding="normal"
          title={
            <Box display={'flex'} alignItems={'center'}>
              <Typography variant="body3" fontWeight={600} color="grey.600">
                {filter.name}
              </Typography>
            </Box>
          }
          action={
            (filter.metadata?.advancedButtonVisible || showClearButton) && (
              <Stack direction="row" gap={0.5} alignItems="center">
                {showClearButton && (
                  <ClearTagsButton
                    type={ClearButtonType.ICON_BUTTON}
                    count={count}
                    onClick={handleClear}
                  />
                )}
                {filter.metadata?.advancedButtonVisible && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="xsmall"
                    onClick={handleAdvancedButtonClick}
                    onMouseDown={handleAdvancedButtonMouseDown}
                  >
                    Advanced
                  </Button>
                )}
              </Stack>
            )
          }
        />
        <CardContent padding="normal">{content}</CardContent>
        {filter.metadata?.advancedButtonVisible && (
          <AdvancedBulkInputDialog
            field={filter.field!}
            dialogState={advancedDialogState}
            metadata={filter.metadata}
            filterType={filter.type}
            filterName={filter.name}
          />
        )}
      </Card>
    );
  }
);

GroupChildrenFilter.displayName = 'GroupChildrenFilter';
